import { apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { parseOrderOverviewResponse } from "@/features/order/api";
import type { OrderOverviewResponse } from "@/features/order/types";
import type {
  DashboardCents,
  DashboardDecimal,
  DashboardDetailKind,
  DashboardOperationsMetadata,
  DashboardPeriodMetadata,
  DashboardRole,
  DashboardRoleAnalytics,
  DashboardRoleDetailResponse,
  DashboardRoleOperations,
  DashboardRankingKind,
  DashboardRankingResponse,
  DashboardRankingSort,
  DashboardSectionName,
  SupplierCurrentLoadsPage,
} from "./types";
import { parseDashboardCents } from "./types";
import { isValidISODate } from "@/lib/dates";
import { serializeDashboardPeriod, type DashboardPeriodState } from "./period";
import { monitorDashboardRequest } from "./monitoring";
import type {
  DashboardAuthorityKeyInput,
  DashboardDetailKeyInput,
  DashboardRankingFetchInput,
  DashboardSupplierAuthority,
} from "./queryKeys";

type JsonObject = Record<string, unknown>;

const asObject = (value: unknown, label: string): JsonObject => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  return value as JsonObject;
};

const asArray = (value: unknown, label: string): unknown[] => {
  if (!Array.isArray(value)) throw new TypeError(`${label} must be an array`);
  return value;
};

const asString = (value: unknown, label: string): string => {
  if (typeof value !== "string" || value.length === 0) {
    throw new TypeError(`${label} must be a nonempty string`);
  }
  return value;
};

const asEnum = <Value extends string>(
  value: unknown,
  values: readonly Value[],
  label: string,
): Value => {
  if (typeof value !== "string" || !values.includes(value as Value)) {
    throw new TypeError(`${label} is invalid`);
  }
  return value as Value;
};

const asCount = (value: unknown, label: string): number => {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${label} must be a nonnegative safe integer`);
  }
  return value;
};

const asHistoryTarget = (value: unknown, label: string): 4 | 8 | 12 => {
  const target = asCount(value, label);
  if (target !== 4 && target !== 8 && target !== 12) {
    throw new TypeError(`${label} must be 4, 8, or 12`);
  }
  return target;
};

const DASHBOARD_DECIMAL = /^[+-]?\d+(?:\.\d+)?$/;

const asDecimal = (value: unknown, label: string): DashboardDecimal => {
  if (typeof value !== "string" || !DASHBOARD_DECIMAL.test(value)) {
    throw new TypeError(`${label} must be a decimal string`);
  }
  return value as DashboardDecimal;
};

const asPercentage = (value: unknown, label: string): DashboardDecimal => {
  const percentage = asDecimal(value, label);
  const numeric = Number(percentage);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
    throw new TypeError(`${label} must be between 0 and 100`);
  }
  return percentage;
};

const asDate = (value: unknown, label: string): string => {
  const date = asString(value, label);
  if (!isValidISODate(date))
    throw new TypeError(`${label} must be an ISO date`);
  return date;
};

const asDateTime = (value: unknown, label: string): string => {
  const date = asString(value, label);
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
      date,
    ) ||
    !isValidISODate(date.slice(0, 10)) ||
    !Number.isFinite(Date.parse(date))
  ) {
    throw new TypeError(`${label} must be an ISO date-time`);
  }
  return date;
};

const parseCentsField = (
  object: JsonObject,
  key: string,
  label: string,
): DashboardCents => {
  const cents = parseDashboardCents(object[key]);
  object[key] = cents;
  return cents;
};

const optionalString = (object: JsonObject, key: string, label: string) => {
  if (object[key] !== undefined) asString(object[key], label);
};

const validateComparison = (value: unknown, label: string) => {
  const comparison = asObject(value, label);
  asEnum(comparison.direction, ["up", "down", "same"], `${label}.direction`);
  asDecimal(comparison.percentage, `${label}.percentage`);
};

type MetricValueKind = "cents" | "count" | "decimal";

const validateMetric = (
  value: unknown,
  unit: "cents" | "count" | "percent",
  valueKind: MetricValueKind,
  label: string,
) => {
  const metric = asObject(value, label);
  const status = asEnum(
    metric.status,
    ["available", "unavailable", "error"],
    `${label}.status`,
  );
  if (metric.unit !== unit) throw new TypeError(`${label}.unit is invalid`);
  if (status === "available") {
    if (Object.hasOwn(metric, "reason_code")) {
      throw new TypeError(
        `${label} available metric cannot contain reason_code`,
      );
    }
    if (valueKind === "cents")
      parseCentsField(metric, "value", `${label}.value`);
    if (valueKind === "count") asCount(metric.value, `${label}.value`);
    if (valueKind === "decimal") asDecimal(metric.value, `${label}.value`);
    if (metric.comparison !== undefined) {
      validateComparison(metric.comparison, `${label}.comparison`);
    }
  } else {
    if (Object.hasOwn(metric, "value") || Object.hasOwn(metric, "comparison")) {
      throw new TypeError(`${label} unavailable metric cannot contain a value`);
    }
    optionalString(metric, "reason_code", `${label}.reason_code`);
  }
};

const validateOperationalCountMetric = (value: unknown, label: string) => {
  const metric = asObject(value, label);
  const status = asEnum(
    metric.status,
    ["available", "unavailable", "error"],
    `${label}.status`,
  );
  if (metric.unit !== "count") throw new TypeError(`${label}.unit is invalid`);
  if (status === "available") {
    if (Object.hasOwn(metric, "reason_code")) {
      throw new TypeError(
        `${label} available metric cannot contain reason_code`,
      );
    }
    asCount(metric.count, `${label}.count`);
  } else {
    if (Object.hasOwn(metric, "count")) {
      throw new TypeError(`${label} unavailable metric cannot contain count`);
    }
    optionalString(metric, "reason_code", `${label}.reason_code`);
  }
};

const validateUnitQuantity = (unit: string, value: unknown, label: string) => {
  const exactQuantity = asDecimal(value, label);
  const normalizedFraction =
    exactQuantity.split(".")[1]?.replace(/0+$/, "") ?? "";
  if (unit === "kg" && normalizedFraction.length > 3) {
    throw new TypeError(`${label}.kg supports at most 3 fractional digits`);
  }
  if (
    (unit === "cx" || unit === "bdj" || unit === "sc" || unit === "un") &&
    !/^[+-]?\d+(?:\.0+)?$/.test(exactQuantity)
  ) {
    throw new TypeError(`${label}.${unit} must be an integer quantity`);
  }
  return exactQuantity;
};

const DASHBOARD_QUANTITY_UNITS = ["kg", "cx", "bdj", "sc", "un"] as const;

const LEGACY_DASHBOARD_QUANTITY_UNITS = ["kg", "cx", "sc", "un"] as const;

const normalizeLegacyCompleteQuantities = (quantities: unknown[]): void => {
  if (quantities.length !== LEGACY_DASHBOARD_QUANTITY_UNITS.length) return;

  const quantitiesByUnit = new Map<string, unknown>();
  for (const item of quantities) {
    if (item === null || typeof item !== "object" || Array.isArray(item))
      return;
    const unit = (item as JsonObject).unit;
    if (typeof unit !== "string" || quantitiesByUnit.has(unit)) return;
    quantitiesByUnit.set(unit, item);
  }

  if (
    quantitiesByUnit.size !== LEGACY_DASHBOARD_QUANTITY_UNITS.length ||
    !LEGACY_DASHBOARD_QUANTITY_UNITS.every((unit) => quantitiesByUnit.has(unit))
  ) {
    return;
  }

  quantities.splice(
    0,
    quantities.length,
    quantitiesByUnit.get("kg"),
    quantitiesByUnit.get("cx"),
    { unit: "bdj", quantity: "0.000" },
    quantitiesByUnit.get("sc"),
    quantitiesByUnit.get("un"),
  );
};

interface ValidateQuantitiesOptions {
  allowEmpty?: boolean;
  coverage?: "complete" | "sparse";
}

const validateQuantities = (
  value: unknown,
  label: string,
  { allowEmpty = false, coverage = "complete" }: ValidateQuantitiesOptions,
) => {
  const quantities = asArray(value, label);
  if (allowEmpty && quantities.length === 0) return;
  if (coverage === "complete") normalizeLegacyCompleteQuantities(quantities);

  const units = new Set<string>();
  quantities.forEach((item, index) => {
    const itemLabel = `${label}[${index}]`;
    const quantity = asObject(item, itemLabel);
    const unit = asEnum(
      quantity.unit,
      DASHBOARD_QUANTITY_UNITS,
      `${itemLabel}.unit`,
    );
    if (units.has(unit)) {
      throw new TypeError(`${label} contains duplicate unit ${unit}`);
    }
    units.add(unit);
    validateUnitQuantity(unit, quantity.quantity, `${itemLabel}.quantity`);
  });
  if (
    coverage === "complete" &&
    units.size !== DASHBOARD_QUANTITY_UNITS.length
  ) {
    throw new TypeError(
      `${label} must contain kg, cx, bdj, sc and un exactly once`,
    );
  }
  if (coverage === "sparse" && units.size === 0) {
    throw new TypeError(`${label} must contain at least one supported unit`);
  }
};

const validateOperationalQuantityMetric = (value: unknown, label: string) => {
  const metric = asObject(value, label);
  const status = asEnum(
    metric.status,
    ["available", "unavailable", "error"],
    `${label}.status`,
  );
  if (metric.unit !== "quantity")
    throw new TypeError(`${label}.unit is invalid`);
  const quantities = asArray(metric.quantities, `${label}.quantities`);
  validateQuantities(quantities, `${label}.quantities`, {
    allowEmpty: status !== "available",
    coverage: "complete",
  });
  if (status === "available" && Object.hasOwn(metric, "reason_code")) {
    throw new TypeError(`${label} available metric cannot contain reason_code`);
  }
  if (status !== "available" && quantities.length !== 0) {
    throw new TypeError(`${label}.quantities must be empty when unavailable`);
  }
  optionalString(metric, "reason_code", `${label}.reason_code`);
};

const assertRolePrivacy = (value: unknown, role: DashboardRole): void => {
  if (Array.isArray(value)) {
    value.forEach((item) => assertRolePrivacy(item, role));
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [rawKey, child] of Object.entries(value)) {
    const key = rawKey.toLowerCase();
    if (role === "supplier" && key.includes("commission")) {
      throw new TypeError(
        "supplier dashboard contains forbidden commission data",
      );
    }
    if (
      (role === "checker" || role === "overviewer") &&
      (key === "value" ||
        key === "price" ||
        key.includes("commission") ||
        key.endsWith("_cents"))
    ) {
      throw new TypeError(
        `${role} dashboard contains forbidden financial field ${rawKey}`,
      );
    }
    if (
      role === "admin_helper" &&
      (key.endsWith("_cents") ||
        [
          "cents",
          "price",
          "value",
          "commission",
          "margin",
          "billing",
          "revenue",
          "financial",
          "finance",
          "cost",
          "amount",
        ].some((forbidden) => key.includes(forbidden)))
    ) {
      throw new TypeError(
        "admin_helper dashboard contains forbidden financial field " + rawKey,
      );
    }
    assertRolePrivacy(child, role);
  }
};

const parseAllDashboardCents = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach(parseAllDashboardCents);
    return;
  }
  if (value === null || typeof value !== "object") return;
  const object = value as JsonObject;
  for (const [key, child] of Object.entries(object)) {
    if (key.toLowerCase().endsWith("_cents")) {
      object[key] = parseDashboardCents(child);
    } else {
      parseAllDashboardCents(child);
    }
  }
};

const validatePeriodMetadata = (value: unknown, label: string) => {
  const period = asObject(value, label);
  const kind = asEnum(
    period.kind,
    ["today", "week", "month", "custom", "all"],
    `${label}.kind`,
  );
  asDate(period.to, `${label}.to`);
  asHistoryTarget(period.requested_history, `${label}.requested_history`);
  asCount(period.available_history, `${label}.available_history`);
  asEnum(
    period.history_unit,
    ["day", "week", "month", "period"],
    `${label}.history_unit`,
  );
  if (typeof period.comparison_available !== "boolean") {
    throw new TypeError(`${label}.comparison_available must be boolean`);
  }
  if (kind === "all") {
    if (
      period.comparison_available !== false ||
      period.history_unit !== "month"
    ) {
      throw new TypeError(`${label} all-period discriminant is invalid`);
    }
    for (const key of [
      "comparison_from",
      "comparison_to",
      "comparison_basis_from",
      "comparison_basis_to",
    ]) {
      if (Object.hasOwn(period, key)) {
        throw new TypeError(`${label}.${key} is forbidden for all period`);
      }
    }
    if (period.from !== undefined) asDate(period.from, `${label}.from`);
  } else {
    if (period.comparison_available !== true) {
      throw new TypeError(`${label} comparable period must expose comparison`);
    }
    for (const key of [
      "from",
      "comparison_from",
      "comparison_to",
      "comparison_basis_from",
      "comparison_basis_to",
    ]) {
      asDate(period[key], `${label}.${key}`);
    }
  }
};

type DefinedKey<Value> = {
  [Key in keyof Value]-?: Exclude<Value[Key], undefined> extends never
    ? never
    : Key;
}[keyof Value];

type AnalyticsSectionName<Role extends DashboardRole> = Extract<
  DefinedKey<DashboardRoleAnalytics<Role>["sections"]>,
  DashboardSectionName
>;

type OperationsSectionName<Role extends DashboardRole> = Extract<
  Exclude<
    DefinedKey<DashboardRoleOperations<Role>>,
    keyof DashboardOperationsMetadata<Role>
  >,
  DashboardSectionName
>;

const ANALYTICS_SECTION_NAMES = {
  super: [
    "load_situation",
    "sales_evolution",
    "sales_heatmap",
    "best_clients",
    "best_stores",
    "supplier_sales",
    "supplier_quality",
    "supplier_impact",
    "leading_products",
    "category_spend",
    "purchased_quantities",
    "quality_outcomes",
  ],
  client: [
    "good_prices",
    "category_spend",
    "purchased_quantities",
    "leading_products",
    "supplier_performance",
    "supplier_impact",
    "stores",
    "purchase_evolution",
    "load_situation",
    "quality_outcomes",
  ],
  client_store: [
    "good_prices",
    "category_spend",
    "purchased_quantities",
    "leading_products",
    "supplier_performance",
    "supplier_impact",
    "purchase_evolution",
    "load_situation",
    "quality_outcomes",
  ],
  supplier: [
    "sales_evolution",
    "leading_products",
    "quantity_sold",
    "stores_served",
    "load_situation",
    "quality_outcomes",
  ],
  checker: [
    "load_situation",
    "checker_highlights",
    "divergence_outcomes",
    "checkin_evolution",
  ],
  overviewer: [
    "load_situation",
    "checker_highlights",
    "divergence_outcomes",
    "checkin_evolution",
  ],
  admin_helper: [
    "load_situation",
    "checker_highlights",
    "divergence_outcomes",
    "checkin_evolution",
  ],
} as const satisfies {
  [Role in DashboardRole]: readonly AnalyticsSectionName<Role>[];
};

const OPERATIONS_SECTION_NAMES = {
  super: ["open_loads", "actions"],
  client: ["open_loads", "current_loads"],
  client_store: ["open_loads", "current_loads"],
  supplier: ["open_loads", "current_loads"],
  checker: ["open_loads", "oldest_open"],
  overviewer: ["open_loads", "oldest_open"],
  admin_helper: ["open_loads", "oldest_open"],
} as const satisfies {
  [Role in DashboardRole]: readonly OperationsSectionName<Role>[];
};

const validateSectionStatus = (
  value: unknown,
  label: string,
  expectedSections: readonly DashboardSectionName[],
) => {
  if (value === undefined) return;
  const statuses = asArray(value, label);
  const seen = new Set<DashboardSectionName>();
  statuses.forEach((item, index) => {
    const section = asObject(item, `${label}[${index}]`);
    const sectionName = asEnum(
      section.section,
      expectedSections,
      `${label}[${index}].section`,
    );
    if (seen.has(sectionName)) {
      throw new TypeError(`${label} contains duplicate section ${sectionName}`);
    }
    seen.add(sectionName);
    asEnum(
      section.status,
      ["available", "unavailable", "error"],
      `${label}[${index}].status`,
    );
    optionalString(section, "reason_code", `${label}[${index}].reason_code`);
  });
};

const validateAnalyticsMetadata = (
  response: JsonObject,
  expectedRole: DashboardRole,
) => {
  if (response.role !== expectedRole) {
    throw new TypeError("dashboard analytics role mismatch");
  }
  validatePeriodMetadata(response.period, "dashboard analytics.period");
  asDateTime(response.generated_at, "dashboard analytics.generated_at");
  if (response.data_as_of !== undefined) {
    asDateTime(response.data_as_of, "dashboard analytics.data_as_of");
  }
  asEnum(
    response.projection_status,
    ["fresh", "stale", "not_ready"],
    "dashboard analytics.projection_status",
  );
  validateSectionStatus(
    response.section_status,
    "dashboard analytics.section_status",
    ANALYTICS_SECTION_NAMES[expectedRole],
  );
};

const validateOperationsMetadata = (
  response: JsonObject,
  expectedRole: DashboardRole,
) => {
  if (response.role !== expectedRole) {
    throw new TypeError("dashboard operations role mismatch");
  }
  asDateTime(response.generated_at, "dashboard operations.generated_at");
  if (response.data_as_of !== undefined) {
    asDateTime(response.data_as_of, "dashboard operations.data_as_of");
  }
  asEnum(
    response.projection_status,
    ["fresh", "stale", "not_ready"],
    "dashboard operations.projection_status",
  );
  validateSectionStatus(
    response.section_status,
    "dashboard operations.section_status",
    OPERATIONS_SECTION_NAMES[expectedRole],
  );
};

const validateMoneySeries = (value: unknown, label: string) => {
  asArray(value, label).forEach((item, index) => {
    const point = asObject(item, `${label}[${index}]`);
    asDate(point.from, `${label}[${index}].from`);
    asDate(point.to, `${label}[${index}].to`);
    parseCentsField(point, "value_cents", `${label}[${index}].value_cents`);
  });
};

const validateCountSeries = (value: unknown, label: string) => {
  asArray(value, label).forEach((item, index) => {
    const point = asObject(item, `${label}[${index}]`);
    asDate(point.from, `${label}[${index}].from`);
    asDate(point.to, `${label}[${index}].to`);
    asCount(point.count, `${label}[${index}].count`);
  });
};

const validateFinancialLoadSituation = (value: unknown, label: string) => {
  const situation = asObject(value, label);
  asCount(situation.total, `${label}.total`);
  const states = asArray(situation.states, `${label}.states`);
  const seenStates = new Set<string>();
  states.forEach((item, index) => {
    const state = asObject(item, `${label}.states[${index}]`);
    seenStates.add(
      asEnum(
        state.state,
        ["not_started", "in_progress", "concluded"],
        `${label}.states[${index}].state`,
      ),
    );
    asCount(state.count, `${label}.states[${index}].count`);
    asPercentage(state.percentage, `${label}.states[${index}].percentage`);
    parseCentsField(
      state,
      "total_value_cents",
      `${label}.states[${index}].total_value_cents`,
    );
  });
  if (states.length !== 3 || seenStates.size !== 3) {
    throw new TypeError(
      `${label}.states must contain each supported state exactly once`,
    );
  }
};

const validateOperationalLoadSituation = (value: unknown, label: string) => {
  const situation = asObject(value, label);
  asCount(situation.total, `${label}.total`);
  const states = asArray(situation.states, `${label}.states`);
  const seenStates = new Set<string>();
  states.forEach((item, index) => {
    const state = asObject(item, `${label}.states[${index}]`);
    seenStates.add(
      asEnum(
        state.state,
        ["not_started", "in_progress", "done"],
        `${label}.states[${index}].state`,
      ),
    );
    asCount(state.count, `${label}.states[${index}].count`);
    asPercentage(state.percentage, `${label}.states[${index}].percentage`);
  });
  if (states.length !== 3 || seenStates.size !== 3) {
    throw new TypeError(
      `${label}.states must contain each supported state exactly once`,
    );
  }
};

const validateMoneyRankings = (
  value: unknown,
  label: string,
  preview = false,
) => {
  const rankings = asArray(value, label);
  if (preview && rankings.length > 5)
    throw new TypeError(`${label} exceeds 5 items`);
  rankings.forEach((item, index) => {
    const ranking = asObject(item, `${label}[${index}]`);
    if (preview && label !== "sections.best_clients") {
      assertKnownKeys(
        ranking,
        ["id", "name", "value_cents"],
        `${label}[${index}]`,
      );
      asString(ranking.id, `${label}[${index}].id`);
    }
    if (ranking.id !== undefined) asString(ranking.id, `${label}[${index}].id`);
    asString(ranking.name, `${label}[${index}].name`);
    parseCentsField(ranking, "value_cents", `${label}[${index}].value_cents`);
  });
};

const validateSupplierQuality = (value: unknown, label: string) => {
  asArray(value, label).forEach((item, index) => {
    const supplier = asObject(item, `${label}[${index}]`);
    asString(supplier.supplier_id, `${label}[${index}].supplier_id`);
    asString(supplier.supplier_name, `${label}[${index}].supplier_name`);
    asDecimal(supplier.return_rate, `${label}[${index}].return_rate`);
    parseCentsField(
      supplier,
      "impact_cents",
      `${label}[${index}].impact_cents`,
    );
    asCount(supplier.supplied_orders, `${label}[${index}].supplied_orders`);
  });
};

const validateQuantityRankings = (value: unknown, label: string) => {
  asArray(value, label).forEach((item, index) => {
    const ranking = asObject(item, `${label}[${index}]`);
    if (ranking.id !== undefined) asString(ranking.id, `${label}[${index}].id`);
    asString(ranking.name, `${label}[${index}].name`);
    validateQuantities(ranking.quantities, `${label}[${index}].quantities`, {
      coverage: "sparse",
    });
  });
};

const validateQualityOutcomes = (
  value: unknown,
  label: string,
  supplier = false,
) => {
  const outcomes = asObject(value, label);
  for (const key of ["decision_count", "substituted_count", "returned_count"]) {
    asCount(outcomes[key], `${label}.${key}`);
  }
  if (supplier) {
    parseCentsField(
      outcomes,
      "return_impact_cents",
      `${label}.return_impact_cents`,
    );
  } else {
    asCount(outcomes.divergence_count, `${label}.divergence_count`);
  }
};

const validateSupplierRankingOutcomes = (value: unknown, label: string) => {
  const outcomes = asObject(value, label);
  for (const key of ["decision_count", "substituted_count", "returned_count"]) {
    asCount(outcomes[key], `${label}.${key}`);
  }
  if (Object.hasOwn(outcomes, "divergence_count")) {
    throw new TypeError(
      `${label}.divergence_count is not attributable to a supplier`,
    );
  }
};

const validateCategorySpend = (value: unknown, label: string) => {
  asArray(value, label).forEach((item, index) => {
    const category = asObject(item, `${label}[${index}]`);
    asString(category.category_name, `${label}[${index}].category_name`);
    parseCentsField(category, "value_cents", `${label}[${index}].value_cents`);
  });
};

const validateClientSections = (
  value: unknown,
  label: string,
  includeStores: boolean,
) => {
  const sections = asObject(value, label);
  asArray(sections.good_prices, `${label}.good_prices`).forEach(
    (item, index) => {
      const price = asObject(item, `${label}.good_prices[${index}]`);
      for (const key of [
        "product_id",
        "product_name",
        "supplier_id",
        "supplier_name",
        "store_id",
        "store_name",
        "unit",
      ]) {
        asString(price[key], `${label}.good_prices[${index}].${key}`);
      }
      parseCentsField(
        price,
        "previous_price_cents",
        `${label}.good_prices[${index}].previous_price_cents`,
      );
      parseCentsField(
        price,
        "current_price_cents",
        `${label}.good_prices[${index}].current_price_cents`,
      );
      asDecimal(
        price.percentage_drop,
        `${label}.good_prices[${index}].percentage_drop`,
      );
    },
  );
  validateCategorySpend(sections.category_spend, `${label}.category_spend`);
  validateQuantities(
    sections.purchased_quantities,
    `${label}.purchased_quantities`,
    { coverage: "complete" },
  );
  validateQuantityRankings(
    sections.leading_products,
    `${label}.leading_products`,
  );
  validateSupplierQuality(
    sections.supplier_performance,
    `${label}.supplier_performance`,
  );
  validateSupplierQuality(sections.supplier_impact, `${label}.supplier_impact`);
  if (includeStores) validateMoneyRankings(sections.stores, `${label}.stores`);
  else if (Object.hasOwn(sections, "stores")) {
    throw new TypeError(`${label}.stores is forbidden for client_store`);
  }
  validateMoneySeries(
    sections.purchase_evolution,
    `${label}.purchase_evolution`,
  );
  validateFinancialLoadSituation(
    sections.load_situation,
    `${label}.load_situation`,
  );
  validateQualityOutcomes(
    sections.quality_outcomes,
    `${label}.quality_outcomes`,
  );
};

const validateOperationalSections = (value: unknown, label: string) => {
  const sections = asObject(value, label);
  validateOperationalLoadSituation(
    sections.load_situation,
    `${label}.load_situation`,
  );
  asArray(sections.checker_highlights, `${label}.checker_highlights`).forEach(
    (item, index) => {
      const checker = asObject(item, `${label}.checker_highlights[${index}]`);
      asString(
        checker.checker_id,
        `${label}.checker_highlights[${index}].checker_id`,
      );
      asString(
        checker.checker_name,
        `${label}.checker_highlights[${index}].checker_name`,
      );
      for (const key of ["checked_items", "divergences", "concluded_loads"]) {
        asCount(checker[key], `${label}.checker_highlights[${index}].${key}`);
      }
    },
  );
  validateQualityOutcomes(
    sections.divergence_outcomes,
    `${label}.divergence_outcomes`,
  );
  validateCountSeries(sections.checkin_evolution, `${label}.checkin_evolution`);
};

const assertKnownKeys = (
  value: JsonObject,
  allowedKeys: readonly string[],
  label: string,
) => {
  for (const key of Object.keys(value)) {
    if (!allowedKeys.includes(key)) {
      throw new TypeError(`${label} contains unknown field ${key}`);
    }
  }
};

const assertAdminHelperActiveQuotationShape = (
  value: unknown,
  label: string,
) => {
  if (value === null) return;
  const quotation = asObject(value, label);
  const allowedKeys = ["id", "date", "status", "item_count"];
  if (
    Object.keys(quotation).length !== allowedKeys.length ||
    Object.keys(quotation).some((key) => !allowedKeys.includes(key))
  ) {
    throw new TypeError(`${label} must contain only safe quotation fields`);
  }
};

const assertAdminHelperPeriodShape = (value: unknown, label: string) => {
  assertKnownKeys(
    asObject(value, label),
    [
      "kind",
      "from",
      "to",
      "requested_history",
      "available_history",
      "history_unit",
      "comparison_available",
      "comparison_from",
      "comparison_to",
      "comparison_basis_from",
      "comparison_basis_to",
    ],
    label,
  );
};

const assertAdminHelperSectionStatusShape = (value: unknown, label: string) => {
  if (value === undefined) return;
  asArray(value, label).forEach((section, index) => {
    assertKnownKeys(
      asObject(section, `${label}[${index}]`),
      ["section", "status", "reason_code"],
      `${label}[${index}]`,
    );
  });
};

const assertAdminHelperComparisonShape = (value: unknown, label: string) => {
  assertKnownKeys(asObject(value, label), ["direction", "percentage"], label);
};

const assertAdminHelperCountMetricShape = (value: unknown, label: string) => {
  const metric = asObject(value, label);
  assertKnownKeys(
    metric,
    ["status", "unit", "count", "comparison", "reason_code"],
    label,
  );
  if (metric.comparison !== undefined) {
    assertAdminHelperComparisonShape(metric.comparison, `${label}.comparison`);
  }
};

const assertAdminHelperQuantityMetricShape = (
  value: unknown,
  label: string,
) => {
  const metric = asObject(value, label);
  assertKnownKeys(
    metric,
    ["status", "unit", "quantities", "reason_code"],
    label,
  );
  asArray(metric.quantities, `${label}.quantities`).forEach(
    (quantity, index) => {
      assertKnownKeys(
        asObject(quantity, `${label}.quantities[${index}]`),
        ["unit", "quantity"],
        `${label}.quantities[${index}]`,
      );
    },
  );
};

const assertAdminHelperAnalyticsShape = (response: JsonObject) => {
  assertKnownKeys(
    response,
    [
      "role",
      "period",
      "generated_at",
      "data_as_of",
      "projection_status",
      "section_status",
      "metrics",
      "sections",
    ],
    "dashboard analytics",
  );
  assertAdminHelperPeriodShape(response.period, "dashboard analytics.period");
  assertAdminHelperSectionStatusShape(
    response.section_status,
    "dashboard analytics.section_status",
  );

  const metrics = asObject(response.metrics, "dashboard analytics.metrics");
  assertKnownKeys(
    metrics,
    [
      "checked_loads",
      "checked_items",
      "open_loads",
      "checked_quantities",
      "divergences",
    ],
    "dashboard analytics.metrics",
  );
  for (const key of [
    "checked_loads",
    "checked_items",
    "open_loads",
    "divergences",
  ]) {
    assertAdminHelperCountMetricShape(metrics[key], `metrics.${key}`);
  }
  assertAdminHelperQuantityMetricShape(
    metrics.checked_quantities,
    "metrics.checked_quantities",
  );

  const sections = asObject(response.sections, "dashboard analytics.sections");
  assertKnownKeys(
    sections,
    [
      "load_situation",
      "checker_highlights",
      "divergence_outcomes",
      "checkin_evolution",
    ],
    "dashboard analytics.sections",
  );

  const loadSituation = asObject(
    sections.load_situation,
    "sections.load_situation",
  );
  assertKnownKeys(
    loadSituation,
    ["total", "states"],
    "sections.load_situation",
  );
  asArray(loadSituation.states, "sections.load_situation.states").forEach(
    (state, index) => {
      assertKnownKeys(
        asObject(state, `sections.load_situation.states[${index}]`),
        ["state", "count", "percentage"],
        `sections.load_situation.states[${index}]`,
      );
    },
  );

  asArray(sections.checker_highlights, "sections.checker_highlights").forEach(
    (checker, index) => {
      assertKnownKeys(
        asObject(checker, `sections.checker_highlights[${index}]`),
        [
          "checker_id",
          "checker_name",
          "checked_items",
          "divergences",
          "concluded_loads",
        ],
        `sections.checker_highlights[${index}]`,
      );
    },
  );

  assertKnownKeys(
    asObject(sections.divergence_outcomes, "sections.divergence_outcomes"),
    [
      "decision_count",
      "substituted_count",
      "returned_count",
      "divergence_count",
    ],
    "sections.divergence_outcomes",
  );
  asArray(sections.checkin_evolution, "sections.checkin_evolution").forEach(
    (point, index) => {
      assertKnownKeys(
        asObject(point, `sections.checkin_evolution[${index}]`),
        ["from", "to", "count"],
        `sections.checkin_evolution[${index}]`,
      );
    },
  );
};

const assertAdminHelperOperationsShape = (response: JsonObject) => {
  assertKnownKeys(
    response,
    [
      "role",
      "generated_at",
      "data_as_of",
      "projection_status",
      "section_status",
      "open_loads",
      "oldest_open",
      "alerts",
      "active_quotation",
    ],
    "dashboard operations",
  );
  assertAdminHelperSectionStatusShape(
    response.section_status,
    "dashboard operations.section_status",
  );
  assertKnownKeys(
    asObject(response.open_loads, "operations.open_loads"),
    ["not_started", "in_progress", "total"],
    "operations.open_loads",
  );
  asArray(response.oldest_open, "operations.oldest_open").forEach(
    (load, index) => {
      assertKnownKeys(
        asObject(load, `operations.oldest_open[${index}]`),
        ["order_id", "label", "state", "created_at"],
        `operations.oldest_open[${index}]`,
      );
    },
  );
  assertKnownKeys(
    asObject(response.alerts, "operations.alerts"),
    ["unresolved", "high", "medium", "low"],
    "operations.alerts",
  );
  assertAdminHelperActiveQuotationShape(
    response.active_quotation,
    "operations.active_quotation",
  );
};

const parseDashboardAnalyticsResponse = <Role extends DashboardRole>(
  value: unknown,
  expectedRole: Role,
): DashboardRoleAnalytics<Role> => {
  const response = asObject(value, "dashboard analytics");
  assertRolePrivacy(response, expectedRole);
  if (expectedRole === "admin_helper")
    assertAdminHelperAnalyticsShape(response);
  parseAllDashboardCents(response);
  validateAnalyticsMetadata(response, expectedRole);
  const metrics = asObject(response.metrics, "dashboard analytics.metrics");
  const sections = asObject(response.sections, "dashboard analytics.sections");
  if (expectedRole !== "super") {
    for (const section of [
      "best_clients",
      "best_stores",
      "sales_heatmap",
      "supplier_sales",
    ]) {
      if (Object.hasOwn(sections, section)) {
        throw new TypeError(`${section} is super-only dashboard data`);
      }
    }
  }

  if (expectedRole === "super") {
    validateMetric(metrics.sales, "cents", "cents", "metrics.sales");
    validateMetric(metrics.open_value, "cents", "cents", "metrics.open_value");
    validateMetric(metrics.orders, "count", "count", "metrics.orders");
    validateMetric(
      metrics.average_ticket,
      "cents",
      "cents",
      "metrics.average_ticket",
    );
    validateMetric(
      metrics.completed_loads,
      "count",
      "count",
      "metrics.completed_loads",
    );
    validateFinancialLoadSituation(
      sections.load_situation,
      "sections.load_situation",
    );
    validateMoneySeries(sections.sales_evolution, "sections.sales_evolution");
    validateMoneyRankings(sections.best_clients, "sections.best_clients", true);
    if (sections.best_stores !== undefined)
      validateMoneyRankings(sections.best_stores, "sections.best_stores", true);
    if (sections.supplier_sales !== undefined)
      validateMoneyRankings(
        sections.supplier_sales,
        "sections.supplier_sales",
        true,
      );
    if (sections.sales_heatmap !== undefined) {
      const cells = asArray(sections.sales_heatmap, "sections.sales_heatmap");
      if (cells.length > 42)
        throw new TypeError("sections.sales_heatmap exceeds 42 cells");
      cells.forEach((value, index) => {
        const label = `sections.sales_heatmap[${index}]`;
        const cell = asObject(value, label);
        const week = asCount(cell.week_of_month, `${label}.week_of_month`);
        const weekday = asCount(cell.weekday, `${label}.weekday`);
        const days = asCount(
          cell.days_with_orders,
          `${label}.days_with_orders`,
        );
        if (week < 1 || week > 6)
          throw new TypeError(`${label}.week_of_month must be 1–6`);
        if (weekday < 1 || weekday > 7)
          throw new TypeError(`${label}.weekday must be 1–7`);
        if (days === 0)
          throw new TypeError(`${label}.days_with_orders must be positive`);
        parseCentsField(
          cell,
          "total_value_cents",
          `${label}.total_value_cents`,
        );
      });
    }
    validateSupplierQuality(
      sections.supplier_quality,
      "sections.supplier_quality",
    );
    validateSupplierQuality(
      sections.supplier_impact,
      "sections.supplier_impact",
    );
    validateQuantityRankings(
      sections.leading_products,
      "sections.leading_products",
    );
    validateCategorySpend(sections.category_spend, "sections.category_spend");
    validateQuantities(
      sections.purchased_quantities,
      "sections.purchased_quantities",
      { coverage: "complete" },
    );
    validateQualityOutcomes(
      sections.quality_outcomes,
      "sections.quality_outcomes",
    );
  } else if (expectedRole === "client" || expectedRole === "client_store") {
    validateMetric(metrics.purchases, "cents", "cents", "metrics.purchases");
    validateMetric(metrics.open_value, "cents", "cents", "metrics.open_value");
    validateMetric(metrics.orders, "count", "count", "metrics.orders");
    validateMetric(
      metrics.no_occurrence_rate,
      "percent",
      "decimal",
      "metrics.no_occurrence_rate",
    );
    validateClientSections(
      sections,
      "dashboard analytics.sections",
      expectedRole === "client",
    );
  } else if (expectedRole === "supplier") {
    validateMetric(metrics.earnings, "cents", "cents", "metrics.earnings");
    validateMetric(metrics.open_value, "cents", "cents", "metrics.open_value");
    validateMetric(metrics.orders, "count", "count", "metrics.orders");
    validateMetric(
      metrics.average_ticket,
      "cents",
      "cents",
      "metrics.average_ticket",
    );
    validateMoneySeries(sections.sales_evolution, "sections.sales_evolution");
    validateQuantityRankings(
      sections.leading_products,
      "sections.leading_products",
    );
    validateQuantities(sections.quantity_sold, "sections.quantity_sold", {
      coverage: "complete",
    });
    validateMoneyRankings(sections.stores_served, "sections.stores_served");
    validateFinancialLoadSituation(
      sections.load_situation,
      "sections.load_situation",
    );
    validateQualityOutcomes(
      sections.quality_outcomes,
      "sections.quality_outcomes",
      true,
    );
  } else {
    validateOperationalCountMetric(
      metrics.checked_loads,
      "metrics.checked_loads",
    );
    validateOperationalCountMetric(
      metrics.checked_items,
      "metrics.checked_items",
    );
    validateOperationalCountMetric(metrics.open_loads, "metrics.open_loads");
    validateOperationalQuantityMetric(
      metrics.checked_quantities,
      "metrics.checked_quantities",
    );
    validateOperationalCountMetric(metrics.divergences, "metrics.divergences");
    validateOperationalSections(sections, "dashboard analytics.sections");
  }

  return response as unknown as DashboardRoleAnalytics<Role>;
};

const validateAnalyticsRequestCorrespondence = (
  responsePeriod: DashboardPeriodMetadata,
  requestPeriod: DashboardPeriodState,
) => {
  if (responsePeriod.kind !== requestPeriod.period) {
    throw new TypeError("dashboard analytics period kind mismatch");
  }
  if (responsePeriod.requested_history !== requestPeriod.history) {
    throw new TypeError("dashboard analytics history mismatch");
  }
  if (
    requestPeriod.period === "custom" &&
    (responsePeriod.from !== requestPeriod.from ||
      responsePeriod.to !== requestPeriod.to)
  ) {
    throw new TypeError("dashboard analytics custom bounds mismatch");
  }
};

const validateFinancialOpenLoads = (value: unknown, label: string) => {
  const loads = asObject(value, label);
  for (const key of ["not_started", "in_progress", "total"]) {
    asCount(loads[key], `${label}.${key}`);
  }
  parseCentsField(loads, "total_value_cents", `${label}.total_value_cents`);
};

const validateOperationalOpenLoads = (value: unknown, label: string) => {
  const loads = asObject(value, label);
  for (const key of ["not_started", "in_progress", "total"]) {
    asCount(loads[key], `${label}.${key}`);
  }
};

const validateCurrentLoads = (value: unknown, label: string) => {
  asArray(value, label).forEach((item, index) => {
    const load = asObject(item, `${label}[${index}]`);
    asString(load.order_id, `${label}[${index}].order_id`);
    asString(load.label, `${label}[${index}].label`);
    asEnum(
      load.state,
      ["not_started", "in_progress"],
      `${label}[${index}].state`,
    );
    parseCentsField(load, "value_cents", `${label}[${index}].value_cents`);
  });
};

const parseSupplierCurrentLoadsPage = (
  value: unknown,
): SupplierCurrentLoadsPage => {
  const page = asObject(value, "supplier current loads");
  assertKnownKeys(
    page,
    [
      "role",
      "generated_at",
      "data_as_of",
      "projection_status",
      "value_status",
      "items",
      "has_more",
      "next_cursor",
    ],
    "supplier current loads",
  );
  assertRolePrivacy(page, "supplier");
  if (page.role !== "supplier")
    throw new TypeError("supplier current loads role mismatch");
  asDateTime(page.generated_at, "supplier current loads.generated_at");
  if (page.data_as_of !== undefined)
    asDateTime(page.data_as_of, "supplier current loads.data_as_of");
  const projection = asEnum(
    page.projection_status,
    ["fresh", "stale", "not_ready"],
    "supplier current loads.projection_status",
  );
  if (projection === "not_ready")
    throw new TypeError("supplier current loads projection not ready");
  if (page.value_status !== "provisional")
    throw new TypeError(
      "supplier current loads value_status must be provisional",
    );
  const items = asArray(page.items, "supplier current loads.items");
  if (items.length > DASHBOARD_DETAIL_PAGE_SIZE)
    throw new TypeError("supplier current loads exceeds page limit");
  const ids = new Set<string>();
  items.forEach((item, index) => {
    const label = `supplier current loads.items[${index}]`;
    const load = asObject(item, label);
    assertKnownKeys(
      load,
      [
        "order_id",
        "business_date",
        "state",
        "label",
        "value_cents",
        "quantities",
      ],
      label,
    );
    const id = asString(load.order_id, `${label}.order_id`);
    if (ids.has(id)) throw new TypeError(`${label}.order_id duplicates a load`);
    ids.add(id);
    asDate(load.business_date, `${label}.business_date`);
    asEnum(load.state, ["not_started", "in_progress"], `${label}.state`);
    asString(load.label, `${label}.label`);
    parseCentsField(load, "value_cents", `${label}.value_cents`);
    validateQuantities(load.quantities, `${label}.quantities`, {
      allowEmpty: true,
      coverage: "sparse",
    });
  });
  validatePagination(page, "supplier current loads");
  return page as unknown as SupplierCurrentLoadsPage;
};

const validateAdminHelperAlerts = (value: unknown, label: string) => {
  const alerts = asObject(value, label);
  for (const key of ["unresolved", "high", "medium", "low"]) {
    asCount(alerts[key], `${label}.${key}`);
  }
};

const validateAdminHelperActiveQuotation = (value: unknown, label: string) => {
  assertAdminHelperActiveQuotationShape(value, label);
  if (value === null) return;
  const quotation = asObject(value, label);
  asString(quotation.id, `${label}.id`);
  asDate(quotation.date, `${label}.date`);
  asEnum(
    quotation.status,
    ["not_started", "in_progress", "completed"],
    `${label}.status`,
  );
  asCount(quotation.item_count, `${label}.item_count`);
};

const parseDashboardOperationsResponse = <Role extends DashboardRole>(
  value: unknown,
  expectedRole: Role,
): DashboardRoleOperations<Role> => {
  const response = asObject(value, "dashboard operations");
  assertRolePrivacy(response, expectedRole);
  if (expectedRole === "admin_helper")
    assertAdminHelperOperationsShape(response);
  parseAllDashboardCents(response);
  validateOperationsMetadata(response, expectedRole);
  if (
    expectedRole === "checker" ||
    expectedRole === "overviewer" ||
    expectedRole === "admin_helper"
  ) {
    validateOperationalOpenLoads(response.open_loads, "operations.open_loads");
    asArray(response.oldest_open, "operations.oldest_open").forEach(
      (item, index) => {
        const load = asObject(item, `operations.oldest_open[${index}]`);
        asString(load.order_id, `operations.oldest_open[${index}].order_id`);
        asString(load.label, `operations.oldest_open[${index}].label`);
        asEnum(
          load.state,
          ["not_started", "in_progress"],
          `operations.oldest_open[${index}].state`,
        );
        asDateTime(
          load.created_at,
          `operations.oldest_open[${index}].created_at`,
        );
      },
    );
    if (expectedRole === "admin_helper") {
      validateAdminHelperAlerts(response.alerts, "operations.alerts");
      validateAdminHelperActiveQuotation(
        response.active_quotation,
        "operations.active_quotation",
      );
    }
  } else {
    validateFinancialOpenLoads(response.open_loads, "operations.open_loads");
    if (expectedRole === "super") {
      asArray(response.actions, "operations.actions").forEach((item, index) => {
        const action = asObject(item, `operations.actions[${index}]`);
        for (const key of ["id", "kind", "label", "priority"]) {
          asString(action[key], `operations.actions[${index}].${key}`);
        }
      });
    } else {
      validateCurrentLoads(response.current_loads, "operations.current_loads");
    }
  }
  return response as unknown as DashboardRoleOperations<Role>;
};

const detailAllowed = (
  role: DashboardRole,
  kind: DashboardDetailKind,
): boolean =>
  role === "super" ||
  ((role === "client" || role === "client_store" || role === "supplier") &&
    kind !== "checker") ||
  ((role === "checker" || role === "overviewer") &&
    (kind === "load" || kind === "checker"));

const validateDetailEntity = (value: unknown, kind: DashboardDetailKind) => {
  const entity = asObject(value, "dashboard detail.entity");
  asString(entity.id, "dashboard detail.entity.id");
  asString(entity.name, "dashboard detail.entity.name");
  if (entity.kind !== kind)
    throw new TypeError("dashboard detail kind mismatch");
  if (kind === "load") {
    asEnum(
      entity.state,
      ["not_started", "in_progress", "done", "unknown"],
      "dashboard detail.entity.state",
    );
  } else if (Object.hasOwn(entity, "state")) {
    throw new TypeError("non-load dashboard detail cannot contain state");
  }
};

const validateLoadLine = (
  value: unknown,
  label: string,
  financial: boolean,
) => {
  const line = asObject(value, label);
  for (const key of ["line_id", "product_id", "product_name", "status"]) {
    asString(line[key], `${label}.${key}`);
  }
  const unit = asString(line.unit, `${label}.unit`);
  validateUnitQuantity(
    unit,
    line.expected_quantity,
    `${label}.expected_quantity`,
  );
  validateUnitQuantity(
    unit,
    line.received_quantity,
    `${label}.received_quantity`,
  );
  if (financial) {
    asString(line.supplier_id, `${label}.supplier_id`);
    asString(line.supplier_name, `${label}.supplier_name`);
    parseCentsField(line, "unit_price_cents", `${label}.unit_price_cents`);
    parseCentsField(line, "value_cents", `${label}.value_cents`);
  }
};

const validatePagination = (page: JsonObject, label = "dashboard detail") => {
  if (typeof page.has_more !== "boolean") {
    throw new TypeError(`${label}.has_more must be boolean`);
  }
  if (page.has_more) {
    asString(page.next_cursor, `${label}.next_cursor`);
  } else if (Object.hasOwn(page, "next_cursor")) {
    throw new TypeError(`completed ${label} must omit next_cursor`);
  }
};

const rankingAllowed = (
  role: DashboardRole,
  kind: DashboardRankingKind,
): boolean => {
  switch (role) {
    case "super":
      return (
        kind === "clients" ||
        kind === "suppliers" ||
        kind === "products" ||
        kind === "stores"
      );
    case "client":
    case "client_store":
      return kind === "suppliers" || kind === "products" || kind === "stores";
    case "supplier":
      return kind === "products" || kind === "stores";
    case "overviewer":
      return kind === "checkers";
    case "checker":
    case "admin_helper":
      return false;
  }
};

const rankingSortAllowed = (
  kind: DashboardRankingKind,
  sort: DashboardRankingSort,
): boolean =>
  kind === "suppliers"
    ? sort === "return_rate" || sort === "financial_impact" || sort === "value"
    : kind === "checkers"
      ? sort === "checked_items"
      : sort === "value";

const validateRankingMoneyItem = (value: unknown, label: string) => {
  const item = asObject(value, label);
  asString(item.id, `${label}.id`);
  asString(item.name, `${label}.name`);
  parseCentsField(item, "value_cents", `${label}.value_cents`);
  validateQuantities(item.quantities, `${label}.quantities`, {
    coverage: "sparse",
  });
};

const parseDashboardRankingResponse = (
  value: unknown,
  role: DashboardRole,
  expectedKind: DashboardRankingKind,
  expectedSort: DashboardRankingSort,
): DashboardRankingResponse => {
  const page = asObject(value, "dashboard ranking");
  assertRolePrivacy(page, role);
  parseAllDashboardCents(page);
  const kind = asEnum(
    page.kind,
    ["clients", "suppliers", "products", "stores", "checkers"],
    "dashboard ranking.kind",
  );
  const sort = asEnum(
    page.sort,
    ["value", "return_rate", "financial_impact", "checked_items"],
    "dashboard ranking.sort",
  );
  if (kind !== expectedKind) {
    throw new TypeError("dashboard ranking kind mismatch");
  }
  if (
    sort !== expectedSort ||
    !rankingSortAllowed(kind, sort) ||
    (kind === "suppliers" && sort === "value" && role !== "super")
  ) {
    throw new TypeError("dashboard ranking sort mismatch");
  }

  const items = asArray(page.items, "dashboard ranking.items");
  items.forEach((value, index) => {
    const label = `dashboard ranking.items[${index}]`;
    if (kind === "clients" || kind === "stores") {
      validateRankingMoneyItem(value, label);
      return;
    }
    if (kind === "products") {
      validateRankingMoneyItem(value, label);
      return;
    }
    if (kind === "suppliers") {
      if (sort === "value") {
        const item = asObject(value, label);
        assertKnownKeys(item, ["id", "name", "value_cents"], label);
        asString(item.id, `${label}.id`);
        asString(item.name, `${label}.name`);
        parseCentsField(item, "value_cents", `${label}.value_cents`);
        return;
      }
      const item = asObject(value, label);
      asString(item.id, `${label}.id`);
      asString(item.name, `${label}.name`);
      asCount(item.supplied_count, `${label}.supplied_count`);
      asCount(item.return_count, `${label}.return_count`);
      asDecimal(item.return_rate, `${label}.return_rate`);
      parseCentsField(
        item,
        "financial_impact_cents",
        `${label}.financial_impact_cents`,
      );
      validateSupplierRankingOutcomes(item.outcomes, `${label}.outcomes`);
      return;
    }
    const item = asObject(value, label);
    asString(item.checker_id, `${label}.checker_id`);
    asString(item.checker_name, `${label}.checker_name`);
    asCount(item.checked_items, `${label}.checked_items`);
    asCount(item.divergences, `${label}.divergences`);
    asCount(item.concluded_loads, `${label}.concluded_loads`);
  });
  validatePagination(page, "dashboard ranking");
  return page as unknown as DashboardRankingResponse;
};

const parseDashboardDetailResponse = <
  Role extends DashboardRole,
  Kind extends DashboardDetailKind,
>(
  value: unknown,
  role: Role,
  kind: Kind,
): DashboardRoleDetailResponse<Role, Kind> => {
  const page = asObject(value, "dashboard detail");
  assertRolePrivacy(page, role);
  parseAllDashboardCents(page);
  validateDetailEntity(page.entity, kind);
  const items = asArray(page.items, "dashboard detail.items");
  const operational = role === "checker" || role === "overviewer";
  items.forEach((item, index) => {
    const label = `dashboard detail.items[${index}]`;
    if (kind === "load") {
      validateLoadLine(item, label, !operational);
    } else if (kind === "checker") {
      const detail = asObject(item, label);
      asString(detail.order_id, `${label}.order_id`);
      asDate(detail.business_date, `${label}.business_date`);
      asEnum(
        detail.state,
        ["not_started", "in_progress", "done", "unknown"],
        `${label}.state`,
      );
      asCount(detail.checked_items, `${label}.checked_items`);
      asCount(detail.divergences, `${label}.divergences`);
      validateQuantities(
        detail.checked_quantities,
        `${label}.checked_quantities`,
        { coverage: "sparse" },
      );
    } else {
      const detail = asObject(item, label);
      asString(detail.order_id, `${label}.order_id`);
      asDate(detail.business_date, `${label}.business_date`);
      asEnum(
        detail.state,
        ["not_started", "in_progress", "done", "unknown"],
        `${label}.state`,
      );
      asString(detail.label, `${label}.label`);
      parseCentsField(detail, "value_cents", `${label}.value_cents`);
      validateQuantities(detail.quantities, `${label}.quantities`, {
        coverage: "sparse",
      });
    }
  });
  validatePagination(page);
  return page as unknown as DashboardRoleDetailResponse<Role, Kind>;
};

export class DashboardStoreScopeMismatchError extends TypeError {}

const validateSelectedStoreResponse = (
  raw: unknown,
  authority: DashboardAuthorityKeyInput,
  label: string,
) => {
  const response = asObject(raw, label);
  if (authority.role === "client_store" && authority.selectedStoreId) {
    if (response.selected_store_id !== authority.selectedStoreId)
      throw new DashboardStoreScopeMismatchError(
        `${label}.selected_store_id missing or mismatched`,
      );
  } else if (Object.hasOwn(response, "selected_store_id")) {
    throw new TypeError(
      `${label}.selected_store_id is forbidden without a selected store`,
    );
  }
};

export async function fetchDashboardV2Analytics<
  Authority extends DashboardAuthorityKeyInput,
>(
  authority: Authority,
  period: DashboardPeriodState,
  signal?: AbortSignal,
): Promise<DashboardRoleAnalytics<Authority["role"]>> {
  const query = new URLSearchParams(serializeDashboardPeriod(period));
  if (authority.role === "client_store" && authority.selectedStoreId)
    query.set("store_id", authority.selectedStoreId);
  return monitorDashboardRequest(
    { endpoint: "analytics", role: authority.role },
    async () => {
      const raw = await apiFetch<unknown>(
        `${ENDPOINTS.dashboard.analytics}?${query}`,
        { signal },
      );
      validateSelectedStoreResponse(raw, authority, "dashboard analytics");
      const response = parseDashboardAnalyticsResponse(raw, authority.role);
      validateAnalyticsRequestCorrespondence(response.period, period);
      return response as unknown as DashboardRoleAnalytics<Authority["role"]>;
    },
  );
}

export async function fetchDashboardV2Operations<
  Authority extends DashboardAuthorityKeyInput,
>(
  authority: Authority,
  signal?: AbortSignal,
): Promise<DashboardRoleOperations<Authority["role"]>> {
  return monitorDashboardRequest(
    { endpoint: "operations", role: authority.role },
    async () => {
      const endpoint =
        authority.role === "client_store" && authority.selectedStoreId
          ? `${ENDPOINTS.dashboard.operations}?store_id=${encodeURIComponent(authority.selectedStoreId)}`
          : ENDPOINTS.dashboard.operations;
      const raw = await apiFetch<unknown>(endpoint, {
        signal,
      });
      validateSelectedStoreResponse(raw, authority, "dashboard operations");
      return parseDashboardOperationsResponse(
        raw,
        authority.role,
      ) as unknown as DashboardRoleOperations<Authority["role"]>;
    },
  );
}

export async function fetchSupplierCurrentLoads(
  authority: DashboardSupplierAuthority,
  cursor?: string,
  signal?: AbortSignal,
): Promise<SupplierCurrentLoadsPage> {
  if (
    authority.role !== "supplier" ||
    !authority.actorId ||
    !authority.supplierId
  )
    throw new TypeError("supplier current loads requires supplier authority");
  const params = new URLSearchParams({
    limit: String(DASHBOARD_DETAIL_PAGE_SIZE),
  });
  if (cursor !== undefined)
    params.set("cursor", asString(cursor, "supplier current loads cursor"));
  return monitorDashboardRequest(
    { endpoint: "operations", role: "supplier" },
    async () =>
      parseSupplierCurrentLoadsPage(
        await apiFetch<unknown>(
          `${ENDPOINTS.dashboard.supplierCurrentLoads}?${params}`,
          { signal },
        ),
      ),
  );
}

export const DASHBOARD_DETAIL_PAGE_SIZE = 20 as const;

export const DASHBOARD_RANKING_PAGE_SIZE = 20 as const;

export async function fetchDashboardV2Ranking(
  input: DashboardRankingFetchInput,
  signal?: AbortSignal,
): Promise<DashboardRankingResponse> {
  if (!rankingAllowed(input.role, input.kind)) {
    throw new TypeError(
      `dashboard ranking ${input.kind} is not allowed for ${input.role}`,
    );
  }
  if (
    !rankingSortAllowed(input.kind, input.sort) ||
    (input.kind === "suppliers" &&
      input.sort === "value" &&
      input.role !== "super")
  ) {
    throw new TypeError("dashboard ranking sort is invalid");
  }
  const params = new URLSearchParams(serializeDashboardPeriod(input.period));
  params.set("sort", input.sort);
  params.set("limit", String(DASHBOARD_RANKING_PAGE_SIZE));
  if (input.cursor !== undefined) {
    params.set("cursor", asString(input.cursor, "dashboard ranking cursor"));
  }
  return monitorDashboardRequest(
    { endpoint: "ranking", role: input.role },
    async () => {
      const raw = await apiFetch<unknown>(
        `${ENDPOINTS.dashboard.ranking(input.kind)}?${params}`,
        { signal },
      );
      return parseDashboardRankingResponse(
        raw,
        input.role,
        input.kind,
        input.sort,
      );
    },
  );
}

export async function fetchDashboardV2Detail<
  Input extends DashboardDetailKeyInput,
>(
  input: Input,
  signal?: AbortSignal,
): Promise<DashboardRoleDetailResponse<Input["role"], Input["kind"]>> {
  if (
    (input.kind === "load" &&
      (input.period !== null || input.sort !== "created")) ||
    (input.kind !== "load" &&
      (input.period === null || input.sort !== "recent"))
  ) {
    throw new TypeError("dashboard detail query contract is invalid");
  }
  if (!detailAllowed(input.role, input.kind)) {
    throw new TypeError(
      `dashboard detail ${input.kind} is not allowed for ${input.role}`,
    );
  }
  const params =
    input.kind === "load"
      ? new URLSearchParams()
      : new URLSearchParams(serializeDashboardPeriod(input.period));
  params.set("limit", String(DASHBOARD_DETAIL_PAGE_SIZE));
  params.set("sort", input.sort);
  if (input.cursor) params.set("cursor", input.cursor);
  return monitorDashboardRequest(
    { endpoint: "detail", role: input.role },
    async () => {
      const raw = await apiFetch<unknown>(
        `${ENDPOINTS.dashboard.detail(input.kind, input.entityId)}?${params}`,
        { signal },
      );
      return parseDashboardDetailResponse(
        raw,
        input.role,
        input.kind,
      ) as unknown as DashboardRoleDetailResponse<Input["role"], Input["kind"]>;
    },
  );
}

export function fetchOrderOverview(
  date: string,
): Promise<OrderOverviewResponse> {
  return apiFetch<unknown>(`${ENDPOINTS.orders.overview}?date=${date}`).then(
    parseOrderOverviewResponse,
  );
}
